<template>
  <section id="docs-list">
    <b-card>
      <div class="docs-list-header mb-2">
        <div class="docs-list-header__intro">
          <h4 class="mb-0">Documentation BUXIDA</h4>
          <p class="text-muted mb-0 mt-1">
            Propositions à valider, articles publiés (visibles sur Chatwoot)
            et archives. Source de vérité : repo <code>ubicx/buxida-docs</code>.
          </p>
        </div>
        <div class="docs-list-header__actions">
          <b-form-input
            v-model="searchTerm"
            size="sm"
            placeholder="Rechercher…"
            class="docs-list-header__search"
          />
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-primary"
            size="sm"
            :disabled="loadingProposals || loadingArticles || loadingArchived || refreshing"
            @click="refresh()"
          >
            <b-spinner v-if="refreshing" small />
            <feather-icon v-else icon="RefreshCwIcon" />
            Rafraîchir
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            size="sm"
            :disabled="syncing"
            :title="'Pousse les articles publiés vers chat.buxida.com'"
            @click="syncChatwoot()"
          >
            <b-spinner v-if="syncing" small />
            <feather-icon v-else icon="UploadCloudIcon" />
            Sync Chatwoot
          </b-button>
          <b-button
            v-ripple.400="'rgba(0, 0, 0, 0.08)'"
            variant="outline-secondary"
            size="sm"
            :disabled="exportingPdf"
            :title="'Génère un PDF de tous les articles publiés, groupés par catégorie'"
            @click="exportPdf()"
          >
            <b-spinner v-if="exportingPdf" small />
            <feather-icon v-else icon="FileTextIcon" />
            Manuel (PDF)
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            size="sm"
            @click="showNewArticle = true"
          >
            <feather-icon icon="PlusIcon" />
            Nouvel article
          </b-button>
        </div>
      </div>

      <new-article-modal
        :visible.sync="showNewArticle"
        @created="onArticleCreated"
        @error="onModalError"
      />

      <b-modal
        id="confirm-archive"
        ref="confirmArchiveModal"
        title="Archiver cet article ?"
        ok-title="Archiver"
        ok-variant="danger"
        cancel-title="Annuler"
        cancel-variant="outline-secondary"
        centered
        no-fade
        @ok="archiveConfirmed()"
      >
        <p>
          L'article <code>{{ pendingArchiveSlug }}</code> sera déplacé dans
          <code>articles/_archived/</code> et son statut Chatwoot passera à
          <strong>archivé</strong>. Il disparaîtra du help center au prochain
          sync.
        </p>
        <p class="mb-0">
          Cette action est réversible depuis l'onglet « Archivés ».
        </p>
      </b-modal>

      <b-tabs v-model="activeTab" content-class="mt-2">
        <!-- ============= ONGLET PROPOSITIONS ============= -->
        <b-tab :title="`Propositions${proposalsCountLabel}`">
          <div v-if="loadingProposals" class="text-center my-3">
            <b-spinner variant="primary" /> Chargement…
          </div>
          <div v-else-if="!proposals.length" class="text-center my-3">
            <feather-icon icon="InboxIcon" size="48" class="text-muted" />
            <p class="text-muted mt-2 mb-0">Aucune proposition en attente.</p>
            <small class="text-muted">
              Lance <code>/update-buxida-docs</code> dans une session Claude Code sur un repo BUXIDA pour en générer, ou clique « Nouvel article » ci-dessus.
            </small>
          </div>
          <vue-good-table
            v-else
            class="docs-list-table"
            :columns="proposalsColumns"
            :rows="proposals"
            :search-options="{ enabled: true, externalQuery: searchTerm }"
            :pagination-options="paginationOptions"
            @on-row-click="onProposalRowClick"
          >
            <template #table-row="props">
              <span
                v-if="props.column.field === 'title'"
                class="font-weight-bold title-cell"
                :title="props.row.title"
              >
                <span class="title">{{ props.row.title }}</span>
                <b-badge v-if="props.row.is_new_article" variant="light-info" class="ml-1">Nouvel article</b-badge>
              </span>
              <span v-else-if="props.column.field === 'proposal_created_at'">
                {{ formatDate(props.row.proposal_created_at) }}
              </span>
              <span
                v-else-if="props.column.field === 'proposal_source'"
                class="text-muted small source-badge"
                :title="props.row.proposal_source || ''"
              >
                <code>{{ props.row.proposal_source || '—' }}</code>
              </span>
              <span v-else-if="props.column.field === 'action'">
                <b-button
                  variant="primary"
                  size="sm"
                  @click.stop="onProposalRowClick({ row: props.row })"
                >
                  Voir
                </b-button>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-tab>

        <!-- ============= ONGLET PUBLIES ============= -->
        <b-tab :title="`Publiés${articlesCountLabel}`">
          <div v-if="loadingArticles" class="text-center my-3">
            <b-spinner variant="primary" /> Chargement…
          </div>
          <div v-else-if="!articles.length" class="text-center my-3">
            <feather-icon icon="InboxIcon" size="48" class="text-muted" />
            <p class="text-muted mt-2 mb-0">Aucun article publié.</p>
          </div>
          <vue-good-table
            v-else
            class="docs-list-table"
            :columns="articlesColumns"
            :rows="articles"
            :search-options="{ enabled: true, externalQuery: searchTerm }"
            :pagination-options="paginationOptions"
          >
            <template #table-row="props">
              <span
                v-if="props.column.field === 'title'"
                class="font-weight-bold title-cell"
                :title="props.row.title"
              >
                <span class="title">{{ props.row.title }}</span>
                <b-badge v-if="props.row.chatwoot_status === 'draft'" variant="light-warning" class="ml-1">Brouillon</b-badge>
              </span>
              <span
                v-else-if="props.column.field === 'category'"
                class="text-muted small"
              >
                {{ formatCategoryLabel(props.row.category) }}
              </span>
              <span v-else-if="props.column.field === 'action'">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1"
                  :disabled="reopening === props.row.slug"
                  @click.stop="reopenArticle(props.row.slug)"
                >
                  <b-spinner v-if="reopening === props.row.slug" small />
                  <feather-icon v-else icon="Edit2Icon" /> Modifier
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  :disabled="archiving === props.row.slug"
                  @click.stop="confirmArchive(props.row.slug)"
                >
                  <b-spinner v-if="archiving === props.row.slug" small />
                  <feather-icon v-else icon="ArchiveIcon" /> Archiver
                </b-button>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-tab>

        <!-- ============= ONGLET ARCHIVES ============= -->
        <b-tab :title="`Archivés${archivedCountLabel}`">
          <div v-if="loadingArchived" class="text-center my-3">
            <b-spinner variant="primary" /> Chargement…
          </div>
          <div v-else-if="!archived.length" class="text-center my-3">
            <feather-icon icon="InboxIcon" size="48" class="text-muted" />
            <p class="text-muted mt-2 mb-0">Aucun article archivé.</p>
          </div>
          <vue-good-table
            v-else
            class="docs-list-table"
            :columns="archivedColumns"
            :rows="archived"
            :search-options="{ enabled: true, externalQuery: searchTerm }"
            :pagination-options="paginationOptions"
          >
            <template #table-row="props">
              <span
                v-if="props.column.field === 'title'"
                class="font-weight-bold title-cell"
                :title="props.row.title"
              >
                <span class="title">{{ props.row.title }}</span>
              </span>
              <span
                v-else-if="props.column.field === 'category'"
                class="text-muted small"
              >
                {{ formatCategoryLabel(props.row.category) }}
              </span>
              <span v-else-if="props.column.field === 'archived_at'">
                {{ formatDate(props.row.archived_at) }}
              </span>
              <span v-else-if="props.column.field === 'action'">
                <b-button
                  variant="outline-success"
                  size="sm"
                  :disabled="restoring === props.row.slug"
                  @click.stop="restoreArchived(props.row.slug)"
                >
                  <b-spinner v-if="restoring === props.row.slug" small />
                  <feather-icon v-else icon="RotateCcwIcon" /> Restaurer
                </b-button>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-tab>
      </b-tabs>
    </b-card>
  </section>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BBadge,
  BFormInput,
  BTabs,
  BTab,
  BModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import NewArticleModal from './NewArticleModal.vue'

const DOCS_API_BASE = 'https://api2.buxida.com'

const CATEGORY_LABELS = {
  demarrer: 'Démarrer',
  'reservation-en-ligne': 'Réservation en ligne',
  'au-quotidien': 'Au quotidien',
  'communication-client': 'Communication client',
  'comptabilite-stats': 'Comptabilité & stats',
  integrations: 'Intégrations',
  aide: 'Aide',
}

export default {
  components: {
    BCard,
    BButton,
    BSpinner,
    BBadge,
    BFormInput,
    BTabs,
    BTab,
    BModal,
    VueGoodTable,
    NewArticleModal,
  },
  directives: { Ripple },
  data() {
    return {
      activeTab: 0,
      loadingProposals: false,
      loadingArticles: false,
      loadingArchived: false,
      articlesLoadedOnce: false,
      archivedLoadedOnce: false,
      refreshing: false,
      syncing: false,
      exportingPdf: false,
      showNewArticle: false,
      searchTerm: '',
      proposals: [],
      articles: [],
      archived: [],
      reopening: null, // slug en cours de reopen
      archiving: null, // slug en cours d'archive
      restoring: null, // slug en cours de restore
      pendingArchiveSlug: null,
      proposalsColumns: [
        { label: 'Titre', field: 'title', sortable: true, width: '32%' },
        { label: 'Slug', field: 'slug', sortable: true, width: '20%' },
        { label: 'Source', field: 'proposal_source', sortable: false },
        { label: 'Soumis', field: 'proposal_created_at', sortable: true, width: '14%' },
        { label: '', field: 'action', sortable: false, width: '90px' },
      ],
      articlesColumns: [
        { label: 'Titre', field: 'title', sortable: true, width: '36%' },
        { label: 'Slug', field: 'slug', sortable: true, width: '24%' },
        { label: 'Catégorie', field: 'category', sortable: true, width: '18%' },
        { label: '', field: 'action', sortable: false, width: '210px' },
      ],
      archivedColumns: [
        { label: 'Titre', field: 'title', sortable: true, width: '32%' },
        { label: 'Slug', field: 'slug', sortable: true, width: '20%' },
        { label: 'Catégorie', field: 'category', sortable: true, width: '18%' },
        { label: 'Archivé le', field: 'archived_at', sortable: true, width: '14%' },
        { label: '', field: 'action', sortable: false, width: '120px' },
      ],
      paginationOptions: {
        enabled: true,
        mode: 'records',
        perPage: 15,
        position: 'top',
        perPageDropdown: [10, 15, 25, 50],
        dropdownAllowAll: false,
        jumpFirstOrLast: true,
        firstLabel: 'Première Page',
        lastLabel: 'Dernière Page',
        nextLabel: 'Suivant',
        prevLabel: 'Précédent',
        rowsPerPageLabel: 'Lignes par page',
        ofLabel: 'sur',
        pageLabel: 'page',
        allLabel: 'Tous',
      },
    }
  },
  computed: {
    proposalsCountLabel() {
      return this.proposals.length ? ` (${this.proposals.length})` : ''
    },
    articlesCountLabel() {
      if (!this.articlesLoadedOnce) return ''
      return this.articles.length ? ` (${this.articles.length})` : ''
    },
    archivedCountLabel() {
      if (!this.archivedLoadedOnce) return ''
      return this.archived.length ? ` (${this.archived.length})` : ''
    },
  },
  watch: {
    activeTab(newTab) {
      // Lazy-load au premier acces des onglets Publies/Archives.
      if (newTab === 1 && !this.articlesLoadedOnce && !this.loadingArticles) {
        this.loadArticles()
      } else if (newTab === 2 && !this.archivedLoadedOnce && !this.loadingArchived) {
        this.loadArchived()
      }
    },
  },
  mounted() {
    this.loadProposals()
  },
  methods: {
    async loadProposals() {
      this.loadingProposals = true
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/proposals`)
        const data = response.data || {}
        if (data.res === 1) {
          this.proposals = Array.isArray(data.items) ? data.items : []
        } else {
          this.toastError('Chargement', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Chargement', this.formatErr(err))
      } finally {
        this.loadingProposals = false
      }
    },
    async loadArticles() {
      // Re-charge sur chaque clic de l'onglet (peu de donnees, evite stale).
      this.loadingArticles = true
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/articles`)
        const data = response.data || {}
        if (data.res === 1) {
          this.articles = Array.isArray(data.items) ? data.items : []
          this.articlesLoadedOnce = true
        } else {
          this.toastError('Chargement', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Chargement', this.formatErr(err))
      } finally {
        this.loadingArticles = false
      }
    },
    async loadArchived() {
      this.loadingArchived = true
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/archived`)
        const data = response.data || {}
        if (data.res === 1) {
          this.archived = Array.isArray(data.items) ? data.items : []
          this.archivedLoadedOnce = true
        } else {
          this.toastError('Chargement', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Chargement', this.formatErr(err))
      } finally {
        this.loadingArchived = false
      }
    },
    async refresh() {
      if (this.refreshing) return
      this.refreshing = true
      try {
        await axios.post(`${DOCS_API_BASE}/admin/docs/refresh`)
        await Promise.all([
          this.loadProposals(),
          this.articlesLoadedOnce ? this.loadArticles() : Promise.resolve(),
          this.archivedLoadedOnce ? this.loadArchived() : Promise.resolve(),
        ])
        this.toastSuccess('Rafraîchi', 'Pull depuis GitHub effectué.')
      } catch (err) {
        this.toastError('Rafraîchissement', this.formatErr(err))
      } finally {
        this.refreshing = false
      }
    },
    async syncChatwoot() {
      if (this.syncing) return
      this.syncing = true
      try {
        const response = await axios.post(`${DOCS_API_BASE}/admin/docs/sync-chatwoot`, {})
        const data = response.data || {}
        if (data.res === 1) {
          const s = (data.result && data.result.summary) || {}
          const synced = s.synced || 0
          const skipped = s.skipped || 0
          const errors = s.errors || 0
          const imgErrs = Array.isArray(s.imageErrors) ? s.imageErrors : []
          let text = `${synced} article${synced > 1 ? 's' : ''} synchronisé${synced > 1 ? 's' : ''} avec Chatwoot.`
          if (skipped) text += ` ${skipped} déjà à jour.`
          if (errors) text += ` ⚠️ ${errors} erreur${errors > 1 ? 's' : ''} de sync.`
          if (imgErrs.length) {
            const sample = imgErrs.slice(0, 3).map(e => `${e.file || ''} ${e.path}: ${e.error}`).join(' | ')
            const more = imgErrs.length > 3 ? ` (+${imgErrs.length - 3} autres)` : ''
            text += ` ⚠️ ${imgErrs.length} image${imgErrs.length > 1 ? 's' : ''} non uploadée${imgErrs.length > 1 ? 's' : ''} (backend: ${s.imageBackend || '?'}) — ${sample}${more}`
          }
          if (errors || imgErrs.length) this.toastError('Sync Chatwoot', text)
          else this.toastSuccess('Sync Chatwoot 🚀', text)
        } else {
          this.toastError('Sync Chatwoot', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Sync Chatwoot', this.formatErr(err))
      } finally {
        this.syncing = false
      }
    },
    async exportPdf() {
      if (this.exportingPdf) return
      this.exportingPdf = true
      try {
        const response = await axios.get(
          `${DOCS_API_BASE}/admin/docs/export-pdf`,
          { responseType: 'blob', timeout: 120000 },
        )
        // Le blob est servi avec Content-Disposition attachment. On
        // declenche le download cote navigateur via objectURL puisque
        // window.open() ne porte pas l'Authorization.
        const date = new Date().toISOString().slice(0, 10)
        const url = URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `manuel-buxida-${date}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 30000)
        this.toastSuccess('Manuel généré 📘', 'Le PDF s\'est ouvert / téléchargé.')
      } catch (err) {
        // Pour les blobs, l'erreur arrive aussi en blob — on essaie de
        // la decoder pour exposer le detail backend.
        let detail = this.formatErr(err)
        if (err.response && err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text()
            const json = JSON.parse(text) || {}
            const { detail: backendDetail } = json
            if (backendDetail) detail = backendDetail
          } catch (_) { /* keep formatErr */ }
        }
        this.toastError('Export PDF', detail)
      } finally {
        this.exportingPdf = false
      }
    },
    onProposalRowClick(payload) {
      const slug = payload && payload.row && payload.row.slug
      if (!slug) return
      this.$router.push({ name: 'docs-detail', params: { slug } })
    },
    onArticleCreated(payload) {
      const slug = payload && payload.slug
      if (!slug) return
      this.toastSuccess('Article créé', `Proposition ${slug} créée. Édite-la pour ajouter le contenu.`)
      this.$router.push({ name: 'docs-detail', params: { slug } })
    },
    onModalError(message) {
      this.toastError('Nouvel article', message || 'Erreur inconnue')
    },
    async reopenArticle(slug) {
      if (this.reopening) return
      this.reopening = slug
      try {
        const response = await axios.post(
          `${DOCS_API_BASE}/admin/docs/articles/${encodeURIComponent(slug)}/reopen`,
        )
        const data = response.data || {}
        if (data.res === 1) {
          this.toastSuccess('Article ouvert en édition', `Proposition créée à partir de ${slug}.`)
          this.$router.push({ name: 'docs-detail', params: { slug } })
        } else {
          this.toastError('Ré-ouverture', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Ré-ouverture', this.formatErr(err))
      } finally {
        this.reopening = null
      }
    },
    confirmArchive(slug) {
      this.pendingArchiveSlug = slug
      this.$refs.confirmArchiveModal.show()
    },
    async archiveConfirmed() {
      const slug = this.pendingArchiveSlug
      this.pendingArchiveSlug = null
      if (!slug || this.archiving) return
      this.archiving = slug
      try {
        const response = await axios.post(
          `${DOCS_API_BASE}/admin/docs/articles/${encodeURIComponent(slug)}/archive`,
        )
        const data = response.data || {}
        if (data.res === 1) {
          this.toastSuccess('Article archivé', `${slug} sera retiré du help center au prochain sync.`)
          await this.loadArticles()
          if (this.archivedLoadedOnce) await this.loadArchived()
        } else {
          this.toastError('Archivage', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Archivage', this.formatErr(err))
      } finally {
        this.archiving = null
      }
    },
    async restoreArchived(slug) {
      if (this.restoring) return
      this.restoring = slug
      try {
        const response = await axios.post(
          `${DOCS_API_BASE}/admin/docs/articles/${encodeURIComponent(slug)}/restore`,
        )
        const data = response.data || {}
        if (data.res === 1) {
          this.toastSuccess('Article restauré', `${slug} sera re-publié au prochain sync.`)
          await this.loadArchived()
          if (this.articlesLoadedOnce) await this.loadArticles()
        } else {
          this.toastError('Restauration', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.toastError('Restauration', this.formatErr(err))
      } finally {
        this.restoring = null
      }
    },
    formatCategoryLabel(slug) {
      if (!slug) return ''
      return CATEGORY_LABELS[slug] || slug
    },
    formatDate(iso) {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        return d.toLocaleString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      } catch (_) { return iso }
    },
    formatErr(err) {
      return (err && err.response && err.response.data && err.response.data.detail)
        || (err && err.message)
        || 'Erreur inconnue'
    },
    toastError(title, text) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'AlertTriangleIcon', variant: 'danger', text },
      }, { timeout: 8000, position: 'top-center' })
    },
    toastSuccess(title, text) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'CheckIcon', variant: 'success', text },
      }, { timeout: 4000, position: 'top-center' })
    },
  },
}
</script>

<style scoped>
::v-deep .vgt-table tbody tr {
  cursor: pointer;
}

.docs-list-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}
.docs-list-header__intro {
  flex: 1 1 320px;
  min-width: 0;
}
.docs-list-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.docs-list-header__search {
  max-width: 240px;
}

.title-cell {
  display: inline-block;
  max-width: 320px;
  vertical-align: middle;
}
.title-cell .title {
  display: inline-block;
  max-width: 100%;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.source-badge {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.docs-list-table ::v-deep .vgt-global-search {
  display: none !important;
}
.docs-list-table ::v-deep .vgt-wrap__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}
.docs-list-table ::v-deep .vgt-wrap__footer .footer__row-count,
.docs-list-table ::v-deep .vgt-wrap__footer .footer__navigation {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}
.docs-list-table ::v-deep .vgt-wrap__footer .footer__navigation {
  margin-left: auto;
}
</style>
